import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, Settings, LogOut, ChevronsUpDown, Newspaper, Users, Building2 } from 'lucide-react';

import SmileLogo from '@/assets/images/Smile.svg';
import { useAuth } from '@/contexts/auth';
import { PermissionGuard } from '@/components/PermissionGuard';
import { CanAccess } from '@/components/CanAccess';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const getCmsNavItems = () => [
  { icon: <Newspaper className="w-5 h-5 mr-2" />, label: 'Reports', to: '/app/reports' },
];

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (_open: boolean) => void;
}

const navLinkClass = (isActive: boolean) =>
  `flex items-center px-4 py-2 rounded transition ${
    isActive ? 'bg-[#F5F5F5] text-gray-900' : 'text-gray-700 hover:bg-[#F5F5F5]'
  }`;

export function Sidebar({ sidebarOpen, setSidebarOpen }: SidebarProps) {
  const { logout, user } = useAuth();
  const location = useLocation();

  return (
    <>
      <div
        className={`
          fixed inset-0 z-50 bg-black/40 md:hidden backdrop-blur-sm
          transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'opacity-100 backdrop-blur-sm' : 'opacity-0 backdrop-blur-none pointer-events-none'}
        `}
        onClick={() => setSidebarOpen(false)}
        aria-label="Fechar menu lateral"
      />
      <aside
        className={`
        bg-[#FAFAFA] flex flex-col justify-between
        w-64 min-h-[100dvh]
        transition-transform duration-300
        fixed top-0 left-0 z-50
        md:translate-x-0 md:z-auto
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:!translate-x-0
      `}
        aria-label="Menu lateral"
      >
        <div>
          <div className="bg-black flex items-center gap-3 p-4">
            <img src={SmileLogo} alt="Culture Monitor SmileLogo" className="w-8 h-8" />
            <div>
              <div className="font-bold text-sm leading-tight text-white">Culture Monitor</div>
              <div className="text-xs text-white font-normal leading-none">v.0</div>
            </div>
          </div>
        </div>
        <div className="border-r flex-1 flex flex-col justify-between">
          <div>
            <h2 className="text-sm font-bold text-[#0A0A0A] px-4 py-2">CMS</h2>
            <nav className="space-y-1 px-2">
              {getCmsNavItems().map((item) => {
                const isActive =
                  item.to === '/app' ? location.pathname === '/app' : location.pathname.startsWith(item.to);
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={() => setSidebarOpen(false)}
                    className={navLinkClass(isActive)}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            <CanAccess roles={['ADMIN', 'MODERATOR', 'CURATOR', 'SUPPORT']}>
              <h2 className="mt-4 text-sm font-bold text-[#0A0A0A] px-4 py-2">Administração</h2>
              <nav className="space-y-1 px-2">
                <PermissionGuard action="view_users">
                  <Link
                    to="/app/settings/users"
                    onClick={() => setSidebarOpen(false)}
                    className={navLinkClass(location.pathname.startsWith('/app/settings/users'))}
                  >
                    <Users className="w-5 h-5 mr-2" />
                    <span>Usuários</span>
                  </Link>
                </PermissionGuard>
                <PermissionGuard action="view_partners">
                  <Link
                    to="/app/settings/partners"
                    onClick={() => setSidebarOpen(false)}
                    className={navLinkClass(location.pathname.startsWith('/app/settings/partners'))}
                  >
                    <Building2 className="w-5 h-5 mr-2" />
                    <span>Marcas</span>
                  </Link>
                </PermissionGuard>
              </nav>
            </CanAccess>
          </div>
          <div className="p-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full min-w-0 rounded transition-colors">
                  <div className="bg-gray-200 rounded-full w-10 h-10 shrink-0 flex items-center justify-center">
                    <User className="w-6 h-6 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0 text-left overflow-hidden">
                    <div className="font-medium text-sm truncate">
                      {user?.name || '[Nome do usuário]'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {user?.email || 'carregando@email.com'}
                    </div>
                  </div>
                  <div className="shrink-0 flex flex-col items-center">
                    <ChevronsUpDown className="w-4 h-4 text-gray-400" />
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/app/settings/account" onClick={() => setSidebarOpen(false)} className="flex items-center gap-2 cursor-pointer">
                    <Settings className="w-4 h-4" />
                    Configurações
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    setSidebarOpen(false);
                    logout();
                  }}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <LogOut className="w-4 h-4" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>
    </>
  );
}
